const $ = require('jquery');
const Cookies = require('./cookie');
const Page = require('./page');

const FORM_SUBMIT_TIMEOUT = 3000;

let isRecaptchaAPILoaded = false;
let recaptchaAPILoadingPromise;

/**
 * Gets Recaptcha form token
 *
 * @param {string} recaptchaSiteKey - Recaptcha site key
 * @param {string} action - Form action
 * @returns {Promise} token request promise
 */
function getRecaptchaToken(recaptchaSiteKey, action) {
    return grecaptcha.execute(recaptchaSiteKey, {action});
}

/**
 * Sets Recaptcha form token to input field
 *
 * @param {string} token - Recaptcha form token
 * @param {string} recaptchaID - Recaptcha field id
 * @returns {void}
 */
function setRecaptchaToken(token, recaptchaID) {
    const recaptcha = document.getElementById(recaptchaID);

    if (!recaptcha) {
        return;
    }

    recaptcha.value = token;
}

/**
 * Initializes Recaptcha input field
 *
 * @param {Object} $recaptcha - Recaptcha input field
 * @returns {Promise} token request promise
 */
function initRecaptcha($recaptcha) {
    const recaptchaSiteKey = $recaptcha.data('sitekey');
    const action = $recaptcha.data('action');
    const recaptchaID = $recaptcha.attr('id');
    return getRecaptchaToken(recaptchaSiteKey, action).then(token => setRecaptchaToken(token, recaptchaID));
}

/**
 * Try to initialize the recaptcha input field within a specified timeout
 *
 * @param {Object} $recaptcha - Recaptcha input field
 * @param {number} $recaptcha - Timeoour in milliseconds
 * @returns {Promise} token request promise
 */
function initRecaptchaWithTimeout($recaptcha, timeout) {
    return new Promise(resolve => {
        const timeoutId = setTimeout(() => resolve(null), timeout);

        initRecaptcha($recaptcha).then(result => {
            clearTimeout(timeoutId);
            resolve(result);
        });
    });
}

/**
 * Verifies if Recaptcha is not allowed by the customer
 *
 * @returns {boolean} true if customer did not accept googlerecaptcha cookie
 */
function isGoogleRecaptchaNotAllowed() {
    const privacySettingsCookie = Cookies.get('privacy_settings') || '';

    return privacySettingsCookie.includes('googlerecaptcha');
}

/**
 * Initializes all Recaptcha input fields
 *
 * @returns {void}
 */
function initializeRecaptchaElements() {
    if (isGoogleRecaptchaNotAllowed()) {
        $('.js-recaptcha-terms').hide();
        return;
    }

    const promiseChain = Promise.resolve();
    grecaptcha.ready(() => {
        $('.js-recaptcha').each((i, recaptcha) => {
            promiseChain.then(() => initRecaptcha($(recaptcha)));
        });
    });
}

/**
 * Refreshes all Recaptcha tokens every 90 secs
 *
 * @returns {void}
 */
function refreshTokens() {
    setInterval(initializeRecaptchaElements, 90000);
}

/**
 * Loads Recaptcha API script if not loaded already
 *
 * @returns {Promise} - Loading promise
 */
function loadRecaptchaAPI() {
    if (!isRecaptchaAPILoaded) {
        const $googleRecaptchaURL = $('.js-google-recaptcha-url');
        const googleRecaptchaURL = $googleRecaptchaURL.data('google-recaptcha-url');

        recaptchaAPILoadingPromise = new Promise(resolve =>
            $.ajax({
                url: googleRecaptchaURL,
                dataType: 'script',
                success: () => {
                    refreshTokens();
                    resolve();
                }
            })
        );

        isRecaptchaAPILoaded = true;
    }

    return recaptchaAPILoadingPromise;
}

/**
 * Updates the recaptcha hidden input with the fresh recaptcha token
 *
 * @param {Object} $recaptcha - Recaptcha input field
 * @returns {Promise} token request promise
 */
export const refreshRecaptchaElement = async $recaptcha => {
    // Ensure that recaptcha API is loaded
    await loadRecaptchaAPI();
    await initRecaptcha($recaptcha);
};

/* eslint-disable import/prefer-default-export */
/**
 * Initializes Recaptcha component
 *
 * @returns {void}
 */
export const init = () => {
    const $googleRecaptchaURL = $('.js-google-recaptcha-url');
    const googleRecaptchaURL = $googleRecaptchaURL.data('google-recaptcha-url');

    if (!googleRecaptchaURL) {
        return;
    }

    $('body').on('privacy::privacySettingsUpdated', () => Page.refresh());

    if (isGoogleRecaptchaNotAllowed()) {
        $('.js-recaptcha-terms').hide();
        return;
    }

    // If there are recaptcha inputs which are not part of the header navigation, trigger API loading immediately
    const $visibleRecapthaInputs = $('.js-recaptcha').filter((_, element) => !$(element).closest('.nav-item').length);
    if ($visibleRecapthaInputs.length) {
        loadRecaptchaAPI().then(initializeRecaptchaElements);
    }

    // Trigger API loading when the header navigation flyout containing the recaptcha input is shown
    $('body').on('header::flyoutshown', (_, element) => {
        if ($(element).find('.js-recaptcha').length) {
            loadRecaptchaAPI().then(initializeRecaptchaElements);
        }
    });

    // Trigger API loading when the newsletter dialog containing the recaptcha input is shown
    $('body').on('newsletter::dialogshown', (_, element) => {
        if ($(element).length) {
            loadRecaptchaAPI().then(function () {
                const promiseChain = Promise.resolve();
                grecaptcha.ready(() => {
                    promiseChain.then(() => initRecaptcha($(element)));
                });
            });
        }
    });

    $('body').on('newsletterfooter::newsletterFormLoaded', () => loadRecaptchaAPI().then(initializeRecaptchaElements));
    $('body').on('membersonlyflyin::membersonlyflyinLoaded', () =>
        loadRecaptchaAPI().then(initializeRecaptchaElements)
    );

    // On form submit try to get a fresh recaptcha token within a set timeout
    $('body').on('submit', '.recaptcha-form', async function (event) {
        const $form = $(this);
        const $recaptcha = $form.find('.js-recaptcha');

        // Check if form has recaptcha enabled
        if (!$recaptcha.length) {
            return;
        }
        event.preventDefault();

        await loadRecaptchaAPI();
        await initRecaptchaWithTimeout($recaptcha, FORM_SUBMIT_TIMEOUT);

        // Append correct action to the form data
        const submitBtn = event.originalEvent.submitter;
        if (submitBtn.name) {
            $form.append($(`<input type="hidden" name="${submitBtn.name}" value="${submitBtn.value}"/>`));
        }

        // Re-submit the form
        event.target.submit();
    });
};
